import type { FormEvent } from 'react';
import React, {
  type ElementRef, memo, useEffect, useRef, useState,
} from '../../../../lib/teact/teact';

import type {
  ApiTokenWithPrice, ApiWalletTransactionCounterparty,
} from '../../../../api/types';
import type { ActivityHistoryFilters as Filters } from './activityHistoryFilters';

import buildClassName from '../../../../util/buildClassName';
import { DEFAULT_HISTORY_FILTERS, hasActivityHistoryFilters } from './activityHistoryFilters';

import useLang from '../../../../hooks/useLang';

import CounterpartyAvatar from '../../../common/CounterpartyAvatar';
import Button from '../../../ui/Button';
import Collapsible from '../../../ui/Collapsible';
import Dropdown from '../../../ui/Dropdown';

import searchStyles from '../../../ui/SearchBar.module.scss';
import styles from './ActivityHistory.module.scss';

import historyIcons from '../../../../assets/activity-history-icons.svg';

type Props = {
  containerRef?: ElementRef<HTMLDivElement>;
  filters: Filters;
  tokens: ApiTokenWithPrice[];
  counterparties: ApiWalletTransactionCounterparty[];
  onChange: (filters: Filters) => void;
};

export default memo(({ containerRef, filters, tokens, counterparties, onChange }: Props) => {
  const lang = useLang();
  const [activePanel, setActivePanel] = useState<'filters' | 'counterparty' | 'dates'>();
  const renderedPanelRef = useRef(activePanel);
  if (activePanel) renderedPanelRef.current = activePanel;
  const renderedPanel = activePanel ?? renderedPanelRef.current;
  const panels = [
    { id: 'filters', icon: 'list-filter', label: lang('$history_filters'),
      isSelected: filters.direction !== 'all' || Boolean(filters.asset) || filters.status !== 'all' },
    { id: 'counterparty', icon: 'hash', label: lang('Counterparty'), isSelected: Boolean(filters.counterparty) },
    { id: 'dates', icon: 'calendar-days', label: lang('$history_dates'),
      isSelected: Boolean(filters.fromDate || filters.toDate) },
  ] as const;
  const update = <K extends keyof Filters>(key: K, value: Filters[K]) => onChange({ ...filters, [key]: value });
  const updateDate = (key: 'fromDate' | 'toDate', value: string) => {
    const next = { ...filters, [key]: value };
    if (next.fromDate && next.toDate && next.fromDate > next.toDate) {
      next[key === 'fromDate' ? 'toDate' : 'fromDate'] = value;
    }
    onChange(next);
  };

  useEffect(() => {
    if (!activePanel) return undefined;

    const scrollContainer = containerRef?.current?.closest<HTMLElement>('.activity-history-scroll');
    if (!scrollContainer) return undefined;

    const closePanel = () => setActivePanel(undefined);
    scrollContainer.addEventListener('scroll', closePanel, { passive: true });

    return () => scrollContainer.removeEventListener('scroll', closePanel);
  }, [activePanel, containerRef]);

  return (
    <div
      ref={containerRef}
      className={styles.filters}
      onKeyDown={(e) => { if (e.key === 'Escape') setActivePanel(undefined); }}
    >
      <div className={styles.searchRow}>
        {panels.map(({ id, icon, label, isSelected }) => (
          <span className={styles.toolWrapper} title={label} key={id}>
            <Button
              isSimple
              className={buildClassName(styles.tool, (activePanel === id || isSelected) && styles.toolActive,
                isSelected && styles.toolSelected)}
              ariaLabel={label}
              onClick={() => setActivePanel(activePanel === id ? undefined : id)}
            >
              <svg className={styles.toolIcon} viewBox="0 0 24 24" aria-hidden="true">
                <use href={`${historyIcons}#${icon}`} />
              </svg>
            </Button>
          </span>
        ))}
        <div className={buildClassName(searchStyles.wrapper, styles.search)}>
          <div className={searchStyles.iconAddon}>
            <svg className={styles.toolIcon} viewBox="0 0 24 24" aria-hidden="true">
              <use href={`${historyIcons}#search`} />
            </svg>
          </div>
          <input
            type="search"
            className={searchStyles.input}
            placeholder={lang('Search')}
            aria-label={lang('Search...')}
            value={filters.query}
            onInput={(e: FormEvent<HTMLInputElement>) => update('query', e.currentTarget.value)}
          />
        </div>
        <span className={styles.toolWrapper} title={lang('Reset')}>
          <Button
            isSimple
            className={styles.tool}
            ariaLabel={lang('Reset')}
            isDisabled={!hasActivityHistoryFilters(filters) && !activePanel}
            onClick={() => {
              onChange(DEFAULT_HISTORY_FILTERS);
              setActivePanel(undefined);
            }}
          >
            <svg className={styles.toolIcon} viewBox="0 0 24 24" aria-hidden="true">
              <use href={`${historyIcons}#circle-x`} />
            </svg>
          </Button>
        </span>
      </div>
      <Collapsible isShown={Boolean(activePanel)}>
        <div className={buildClassName(styles.filterPanel, activePanel && styles.filterPanelOpen)}>
          {renderedPanel === 'filters' && (
            <div className={styles.filterGrid}>
              <Dropdown
                withPortal
                withCustomScrollbar
                label={lang('Activity')}
                className={buildClassName(styles.filterField, filters.direction !== 'all' && styles.fieldSelected)}
                labelClassName={styles.fieldLabel}
                buttonClassName={styles.selectButton}
                arrow="chevron"
                selectedValue={filters.direction}
                items={[
                  { value: 'all', name: lang('All') }, { value: 'incoming', name: lang('Received') },
                  { value: 'outgoing', name: lang('Sent') }, { value: 'swap', name: lang('Swap') },
                ]}
                onChange={(value: Filters['direction']) => update('direction', value)}
              />
              <Dropdown
                withPortal
                withCustomScrollbar
                label={lang('Asset')}
                className={buildClassName(styles.filterField, filters.asset && styles.fieldSelected)}
                labelClassName={styles.fieldLabel}
                buttonClassName={styles.selectButton}
                arrow="chevron"
                selectedValue={filters.asset}
                items={[
                  { value: '', name: lang('All') },
                  ...tokens.map((token) => ({ value: token.slug, name: token.symbol })),
                ]}
                onChange={(value) => update('asset', value)}
              />
              <Dropdown
                withPortal
                withCustomScrollbar
                label={lang('$history_status')}
                className={buildClassName(styles.filterField, filters.status !== 'all' && styles.fieldSelected)}
                labelClassName={styles.fieldLabel}
                buttonClassName={styles.selectButton}
                arrow="chevron"
                selectedValue={filters.status}
                items={[
                  { value: 'all', name: lang('All') }, { value: 'pending', name: lang('$history_pending') },
                  { value: 'completed', name: lang('$history_completed') }, { value: 'failed', name: lang('Failed') },
                ]}
                onChange={(value: Filters['status']) => update('status', value)}
              />
            </div>
          )}
          {renderedPanel === 'counterparty' && (
            <div className={styles.filterGrid} title={filters.counterparty || undefined} data-telemetry-block="">
              <Dropdown
                withPortal
                withCustomScrollbar
                label={lang('Counterparty')}
                className={buildClassName(styles.filterField, filters.counterparty && styles.fieldSelected)}
                labelClassName={styles.fieldLabel}
                buttonClassName={styles.selectButton}
                arrow="chevron"
                selectedValue={filters.counterparty}
                items={[{ value: '', name: lang('All') }, ...counterparties.map((counterparty) => ({
                  value: counterparty.id, name: counterparty.name,
                  isTelemetryBlocked: true,
                  icon: () => <CounterpartyAvatar counterparty={counterparty} />,
                }))]}
                onChange={(value) => update('counterparty', value)}
              />
            </div>
          )}
          {renderedPanel === 'dates' && (
            <div className={styles.dates}>
              {(['fromDate', 'toDate'] as const).map((key) => (
                <label className={buildClassName(styles.dateField, filters[key] && styles.fieldSelected)} key={key}>
                  <span>{lang(key === 'fromDate' ? '$history_date_from' : '$history_date_to')}</span>
                  <input
                    type="date"
                    value={filters[key]}
                    max={key === 'fromDate' ? filters.toDate || undefined : undefined}
                    min={key === 'toDate' ? filters.fromDate || undefined : undefined}
                    onInput={(e: FormEvent<HTMLInputElement>) => updateDate(key, e.currentTarget.value)}
                  />
                </label>
              ))}
            </div>
          )}
        </div>
      </Collapsible>
    </div>
  );
});
